import { Button, Col, Form, Input, Row, Divider, Tree, Layout, message, ColorPicker, Tooltip, Select } from 'antd';
import { useContext, useEffect, useMemo, useState } from 'react';
import {
  getAllNodesByProjectId,
  deleteNode,
  updateNode,
  createNode,
  moveNodeDown,
  moveNodeUp,
  cloneNode,
} from '../../../features/projects/nodeSlice';
import { useDispatch, useSelector } from 'react-redux';
import { NotificationContext } from '../../../providers/NotificationProvider';
import { AppstoreOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DeviationType, MethodologyLabels, MethodologyType, NodeType, ProjectType, TreeItemType } from '../../../types/types';
import { getProject, getProjectTreeView } from '../../../features/projects/projectSlice';
import Deviations from './Deviations/Deviations';
import { getAllDeviationsByNodeId } from '../../../features/projects/deviationSlice';
import type { DataNode, EventDataNode } from 'antd/es/tree';
import Sider from 'antd/es/layout/Sider';
import { Content } from 'antd/es/layout/layout';
import Deviation from './Deviations/Deviation';
import NodeList from './NodeList';
import { defineColorText } from '../../../features/utils/utils';
import MatrixViewerOverlay from '../matrix/MatrixViewerOverlay';
import { useTranslation } from 'react-i18next';
import { get } from '../../../api/base';

const buildTreeDataWithOrder = (
  nodes: TreeItemType[]
): {
  treeData: (DataNode & { originalData?: TreeItemType })[];
  nodeOrderMap: Record<number, number>;
  deviationOrderMap: Record<number, { nodeId: number; order: number }>;
} => {
  const nodeOrderMap: Record<number, number> = {};
  const deviationOrderMap: Record<number, { nodeId: number; order: number }> = {};
  const deviationCounters: Record<number, number> = {};
  let nodeSequence = 1;

  const convert = (
    items: TreeItemType[] | Record<string, TreeItemType> | null | undefined,
    parentNodeId?: number
  ): (DataNode & { originalData?: TreeItemType })[] => {
    if (!items) return [];

    const iterableItems: TreeItemType[] = Array.isArray(items)
      ? items
      : (Object.values(items).filter(Boolean) as TreeItemType[]);

    if (iterableItems.length === 0) return [];

    return iterableItems.map((item) => {
      let prefix = '';
      if (item.type === 'node' && item.id !== null && item.id !== undefined) {
        nodeOrderMap[item.id] = nodeSequence;
        prefix = `${nodeSequence}. `;
        deviationCounters[item.id] = 1;
        nodeSequence += 1;
      } else if (item.type === 'deviation' && item.id !== null && item.id !== undefined) {
        const parentId = parentNodeId ?? 0;
        const currentOrder = deviationCounters[parentId] ?? 1;
        deviationOrderMap[item.id] = { nodeId: parentId, order: currentOrder };
        prefix = `${currentOrder}. `;
        deviationCounters[parentId] = currentOrder + 1;
      }

      const nextParentId = item.type === 'node' && item.id !== null && item.id !== undefined ? item.id : parentNodeId;

      const methodologyName = item.type === 'node' ? item.methodology_name : null;
      return {
        key: `${item.type}-${item.id}`,
        title: (
          <span
            style={{
              background: item.color || (item.type === 'deviation' ? item.color : undefined) || 'transparent',
              color:
                item.type === 'deviation' || item.type === 'node'
                  ? defineColorText(item.color || '#ffffff')
                  : '#000000',
              padding: '2px 8px',
              borderRadius: '4px',
              display: 'inline-block',
            }}
          >
            {`${prefix}${item.name}`}{' '}
            {methodologyName ? (
              <small style={{ fontSize: '0.6em', color: '#9aa0a6' }}>{`(${methodologyName})`}</small>
            ) : null}
          </span>
        ),
        children: item.children && Array.isArray(item.children) ? convert(item.children, nextParentId) : [],
        originalData: item,
      };
    });
  };

  const treeData = convert(nodes);
  return { treeData, nodeOrderMap, deviationOrderMap };
};

interface TreeNavigationProps {
  data: TreeItemType[];
  treeData: (DataNode & { originalData?: TreeItemType })[];
  selectedType: string;
  selectedId: number;
  onSelectionChange: (type: string, id: number) => void;
}

const TreeNavigation: React.FC<TreeNavigationProps> = ({
  data,
  treeData,
  selectedType,
  selectedId,
  onSelectionChange,
}) => {
  const { t, i18n } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [autoExpandParent, setAutoExpandParent] = useState(true);

  useEffect(() => {
    if (selectedType && selectedId) {
      const findParentKeys = (
        nodes: TreeItemType[],
        targetId: number,
        targetType: string,
        path: string[] = []
      ): string[] | null => {
        for (const node of nodes) {
          const currentPath = [...path, `${node.type}-${node.id}`];

          if (node.id === targetId && node.type === targetType) {
            return currentPath;
          }

          if (node.children) {
            const found = findParentKeys(node.children, targetId, targetType, currentPath);
            if (found) return found;
          }
        }
        return null;
      };

      const parentKeys = findParentKeys(data, selectedId, selectedType);
      if (parentKeys) {
        setExpandedKeys(parentKeys);
        setAutoExpandParent(true);
      }
    }
  }, [selectedType, selectedId, data]);

  const handleNodeSelect = async (
    selectedKeys: React.Key[],
    info: Parameters<NonNullable<React.ComponentProps<typeof Tree>['onSelect']>>[1]
  ) => {
    const node = info.node as unknown as EventDataNode<TreeItemType>;
    const originalData = (node as EventDataNode<TreeItemType> & { originalData?: TreeItemType }).originalData;

    if (!originalData) return;

    const { type, id } = originalData;

    try {
      messageApi.loading({ content: t('Cargando...'), key: 'loading' });
      // Llamamos a la función del padre para actualizar los valores
      onSelectionChange(type, id ?? 0);
    } catch (error) {
      messageApi.error({
        content: t('Error al cargar {{type}} con ID {{id}}', { type, id }),
        key: 'loading',
      });
      console.error('Error:', error);
    }
  };

  const handleExpand = (expandedKeysValue: React.Key[]) => {
    setExpandedKeys(expandedKeysValue);
    setAutoExpandParent(false);
  };

  return (
    <>
      {contextHolder}
      <div style={{ padding: '20px', maxWidth: '400px' }}>
        <Tree
          showLine
          treeData={treeData}
          onSelect={handleNodeSelect}
          onExpand={handleExpand}
          expandedKeys={expandedKeys}
          autoExpandParent={autoExpandParent}
          selectedKeys={selectedType && selectedId ? [`${selectedType}-${selectedId}`] : []}
        />
      </div>
    </>
  );
};

const Nodes: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { project_id } = useParams();

  const { triggerNotification } = useContext(NotificationContext);
  const { nodes } = useSelector((store: any) => store.node);
  const { access } = useSelector((store: any) => store.user);
  const [form] = Form.useForm();
  const [isNew, setIsNew] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentProject, setCurrentProject] = useState<ProjectType>();
  const { currentProjectTreeView } = useSelector((store: any) => store.project);
  const [methodologies, setMethodologies] = useState<MethodologyType[]>([]);
  const [loadingMethodologies, setLoadingMethodologies] = useState(false);

  const { treeData, nodeOrderMap, deviationOrderMap } = useMemo(
    () => buildTreeDataWithOrder(currentProjectTreeView ?? []),
    [currentProjectTreeView]
  );

  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedId, setSelectedId] = useState<number>(0);

  const [deviations, setDeviations] = useState<DeviationType[]>([]);
  const [siderCollapsed, setSiderCollapsed] = useState(false);
  const [isMatrixOverlayVisible, setIsMatrixOverlayVisible] = useState(false);

  const resolveMethodology = (value: number | MethodologyType | null | undefined): MethodologyType | null => {
    return value && typeof value === 'object' ? value : null;
  };

  const projectMethodology = resolveMethodology(currentProject?.methodology);
  const projectLabels: MethodologyLabels | undefined = projectMethodology?.labels;
  const nodesLabel = projectLabels?.nodes ?? t('Nodos');

  const loadMethodologies = () => {
    setLoadingMethodologies(true);
    const headers = access ? { Authorization: `Bearer ${access}` } : undefined;
    get('methodologies/', { headers })
      .then((response: any) => {
        setMethodologies(response?.data ?? []);
      })
      .catch(() => {
        setMethodologies([]);
      })
      .finally(() => setLoadingMethodologies(false));
  };

  const selectedDeviationOrderInfo =
    selectedType === 'deviation' && selectedId ? deviationOrderMap[selectedId] : undefined;
  const selectedDeviationOrder = selectedDeviationOrderInfo?.order;
  const selectedDeviationParentNodeId = selectedDeviationOrderInfo?.nodeId ?? undefined;
  const selectedDeviationNodeOrder =
    selectedDeviationParentNodeId && selectedDeviationParentNodeId > 0
      ? nodeOrderMap[selectedDeviationParentNodeId] ?? undefined
      : undefined;

  const handleSelectionChange = (type: string, id: number) => {
    setSelectedType(type);
    setSelectedId(id);
  };

  useEffect(() => {
    // if project_id is null, undefined or 0 or not a number navigate to projects
    if (!project_id || isNaN(parseInt(project_id))) {
      navigate('/proyectos');
      return;
    } else {
      dispatch(getProject(parseInt(project_id as string)) as any)
        .then((result: any) => {
          if (getProject.fulfilled.match(result)) {
            setCurrentProject(result.payload);
            triggerNotification('success', t('Éxito'), t('Se cargó el proyecto correctamente'), 'bottomRight');
          } else {
            triggerNotification('error', t('Error'), t('Error al cargar el proyecto'), 'bottomRight');
          }
        })
        .catch((err: any) => {
          triggerNotification('error', t('Error'), t('Error al cargar el proyecto'), 'bottomRight');
        });
      getProjectTree();
      getNodes();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!project_id || isNaN(parseInt(project_id))) {
      return;
    }
    dispatch(getProject(parseInt(project_id as string)) as any)
      .then((result: any) => {
        if (getProject.fulfilled.match(result)) {
          setCurrentProject(result.payload);
        }
      })
      .catch(() => {});
    dispatch(getAllNodesByProjectId(parseInt(project_id as string)) as any).catch(() => {});
    dispatch(getProjectTreeView(parseInt(project_id as string)) as any).catch(() => {});
    // eslint-disable-next-line
  }, [i18n.language]);

  useEffect(() => {
    loadMethodologies();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (selectedId > 0) {
      if (selectedType === 'node') {
        getDeviations(selectedId);
      }
    } else {
      setDeviations([]);
    }
    // eslint-disable-next-line
  }, [selectedId, selectedType]);

  const getNodes = () => {
    dispatch(getAllNodesByProjectId(parseInt(project_id as string)) as any)
      .then((result: any) => {
        if (getAllNodesByProjectId.fulfilled.match(result)) {
          triggerNotification('success', t('Éxito'), t('Se cargaron los nodos correctamente'), 'bottomRight');
        } else {
          triggerNotification('error', t('Error'), t('Error al cargar los nodos'), 'bottomRight');
        }
      })
      .catch((err: any) => {
        triggerNotification('error', t('Error'), t('Error al cargar los nodos'), 'bottomRight');
      });
  };

  const getProjectTree = () => {
    dispatch(getProjectTreeView(parseInt(project_id as string)) as any)
      .then((result: any) => {
        if (getProjectTreeView.fulfilled.match(result)) {
          triggerNotification('success', t('Éxito'), t('Se cargó el árbol del proyecto correctamente'), 'bottomRight');
        } else {
          triggerNotification('error', t('Error'), t('Error al cargar el árbol del proyecto'), 'bottomRight');
        }
      })
      .catch((err: any) => {
        triggerNotification('error', t('Error'), t('Error al cargar el árbol del proyecto'), 'bottomRight');
      });
  };

  const getDeviations = (nodeId: number) => {
    if (nodeId === 0) return;

    dispatch(getAllDeviationsByNodeId(nodeId) as any)
      .then((result: any) => {
        if (getAllDeviationsByNodeId.fulfilled.match(result)) {
          setDeviations(result.payload);
        }
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const handleDelete = (item: any) => {
    if (window.confirm(t('¿Estás seguro de eliminar este nodo?'))) {
      const nodeDelete: NodeType = {
        id: item.id,
        name: '',
        project: {
          id: parseInt(project_id as string),
          name: '',
        },
      };
      dispatch(deleteNode(nodeDelete) as any)
        .then((result: any) => {
          if (deleteNode.fulfilled.match(result)) {
            triggerNotification('success', t('Éxito'), t('Se eliminó el nodo correctamente'), 'bottomRight');
            getNodes();
            getProjectTree();
          } else {
            triggerNotification('error', t('Error'), t('Error al eliminar el nodo'), 'bottomRight');
          }
        })
        .catch((err: any) => {
          triggerNotification('error', t('Error'), t('Error al eliminar el nodo'), 'bottomRight');
        });
    }
  };

  const moveUp = (node: NodeType) => {
    if (!node.id) return;
    dispatch(moveNodeUp(node.id) as any)
      .then((result: any) => {
        if (moveNodeUp.fulfilled.match(result)) {
          triggerNotification('success', t('Éxito'), t('El nodo se movió hacia arriba correctamente'), 'bottomRight');
          getNodes();
          getProjectTree();
        } else {
          triggerNotification('error', t('Error'), t('Error al mover el nodo hacia arriba'), 'bottomRight');
        }
      })
      .catch((err: any) => {
        triggerNotification('error', t('Error'), t('Error al mover el nodo hacia arriba'), 'bottomRight');
      });
  };

  const moveDown = (node: NodeType) => {
    if (!node.id) return;
    dispatch(moveNodeDown(node.id) as any)
      .then((result: any) => {
        if (moveNodeDown.fulfilled.match(result)) {
          triggerNotification('success', t('Éxito'), t('El nodo se movió hacia abajo correctamente'), 'bottomRight');
          getNodes();
          getProjectTree();
        } else {
          triggerNotification('error', t('Error'), t('Error al mover el nodo hacia abajo'), 'bottomRight');
        }
      })
      .catch((err: any) => {
        triggerNotification('error', t('Error'), t('Error al mover el nodo hacia abajo'), 'bottomRight');
      });
  };

  const handleClone = (node: NodeType) => {
    if (!node.id) return;
    const projectId = parseInt(project_id as string);
    dispatch(cloneNode({ projectId, nodeId: node.id }) as any)
      .then((result: any) => {
        if (cloneNode.fulfilled.match(result)) {
          triggerNotification('success', t('Éxito'), t('Se clonó el nodo correctamente'), 'bottomRight');
          getNodes();
          getProjectTree();
        } else {
          triggerNotification('error', t('Error'), t('Error al clonar el nodo'), 'bottomRight');
        }
      })
      .catch(() => {
        triggerNotification('error', t('Error'), t('Error al clonar el nodo'), 'bottomRight');
      });
  };

  const onFinish = async (values: any) => {
    if (isEdit) {
      // Update node
      const node: NodeType = {
        id: values.id,
        name: values.name,
        description: values.description,
        design_intention: values.design_intention,
        special_consideration: values.special_consideration,
        comments: values.comments,
        color: values.color,
        methodology: values.methodology ?? null,
        project: {
          id: parseInt(project_id as string),
          name: '',
        },
      };
      dispatch(updateNode(node) as any)
        .then((result: any) => {
          if (updateNode.fulfilled.match(result)) {
            triggerNotification('success', t('Éxito'), t('Se actualizó el nodo correctamente'), 'bottomRight');
            setIsEdit(false);
            form.resetFields();
            getNodes();
            getProjectTree();
          } else {
            triggerNotification('error', t('Error'), t('Error al actualizar el nodo'), 'bottomRight');
          }
        })
        .catch((err: any) => {
          triggerNotification('error', t('Error'), t('Error al actualizar el nodo'), 'bottomRight');
        });
      return;
    }
    const node: NodeType = {
      id: null,
      name: values.name,
      description: values.description,
      design_intention: values.design_intention,
      special_consideration: values.special_consideration,
      comments: values.comments,
      color: values.color,
      methodology: values.methodology ?? null,
      project: {
        id: parseInt(project_id as string),
        name: '',
      },
    };
    dispatch(createNode(node) as any)
      .then((result: any) => {
        if (createNode.fulfilled.match(result)) {
          triggerNotification('success', t('Éxito'), t('Se creó el nodo correctamente'), 'bottomRight');
          setIsNew(false);
          form.resetFields();
          getNodes();
          getProjectTree();
        } else {
          triggerNotification('error', t('Error'), t('Error al crear el nodo'), 'bottomRight');
        }
      })
      .catch((err: any) => {
        triggerNotification('error', t('Error'), t('Error al crear el nodo'), 'bottomRight');
      });
  };

  const handleEdit = (node: NodeType) => {
    const methodologyValue =
      typeof node.methodology === 'object' ? node.methodology?.id : node.methodology ?? null;
    form.setFieldsValue({
      id: node.id,
      name: node.name,
      description: node.description,
      design_intention: node.design_intention,
      special_consideration: node.special_consideration,
      comments: node.comments,
      color: node.color,
      methodology: methodologyValue,
    });
    setIsEdit(true);
  };

  return (
    <>
      <Row className='content'>
        <Col style={{ width: '100%' }}>
          <div className='title-full'>
            <h1>
              <Link to={`/proyecto/${currentProject?.id}/ver`}>{currentProject?.name}</Link>
            </h1>
            {isNew || isEdit ? (
              <Button
                type='primary'
                danger
                onClick={() => {
                  setIsNew(false);
                  setIsEdit(false);
                }}
              >
                {t('Cancelar')}
              </Button>
            ) : (
              <Button
                type='primary'
                onClick={() => {
                  form.resetFields();
                  setIsNew(true);
                }}
              >
                {t('Nuevo Nodo')}
              </Button>
            )}
          </div>
          <div className='ant-divider'></div>
          {(isNew || isEdit) && (
            <Form form={form} layout='vertical' onFinish={onFinish}>
              <Form.Item hidden name='id'>
                <Input />
              </Form.Item>
              <Form.Item
                label={t('Nombre')}
                name='name'
                rules={[{ required: true, message: t('Por favor ingrese el nombre') }]}
              >
                <Input placeholder={t('Nombre del nodo')} />
              </Form.Item>
              <Form.Item label={t('Descripción')} name='description'>
                <Input.TextArea placeholder={t('Descripción del nodo')} style={{ height: 100 }} />
              </Form.Item>
              <Form.Item label={t('Intención de diseño')} name='design_intention'>
                <Input.TextArea placeholder={t('Intención de diseño')} style={{ height: 100 }} />
              </Form.Item>
              <Form.Item label={t('P&ID')} name='special_consideration'>
                <Input.TextArea placeholder={t('P&ID')} style={{ height: 100 }} />
              </Form.Item>
              <Form.Item label={t('Comentarios')} name='comments'>
                <Input.TextArea placeholder={t('Comentarios')} style={{ height: 100 }} />
              </Form.Item>
              <Form.Item label={t('Metodología')} name='methodology'>
                <Select
                  allowClear
                  loading={loadingMethodologies}
                  placeholder={t('Metodología del proyecto')}
                  options={methodologies.map((methodology) => ({
                    label: methodology.name,
                    value: methodology.id,
                  }))}
                />
              </Form.Item>
              <Form.Item name='color' label={t('Color')}>
                <ColorPicker
                  format='hex'
                  value={form.getFieldValue('color')}
                  onChange={(aggregate, color) => {
                    const hexColor = aggregate.toHexString();
                    console.log(hexColor, color);
                    form.setFieldsValue({ color: hexColor || color });
                  }}
                />
              </Form.Item>
              <Form.Item>
                <Button type='primary' htmlType='submit'>
                  {t('Guardar')}
                </Button>
                <Button
                  type='primary'
                  danger
                  onClick={() => {
                    setIsNew(false);
                    setIsEdit(false);
                  }}
                  style={{ marginLeft: '10px' }}
                >
                  {t('Cancelar')}
                </Button>
              </Form.Item>
            </Form>
          )}
          <Layout style={{ padding: '24px 0', background: '#fff', position: 'relative' }}>
            <Sider
              style={{ background: '#f2f2f2', overflow: 'hidden' }}
              width={350}
              collapsedWidth={32}
              collapsible
              collapsed={siderCollapsed}
              onCollapse={(collapsed) => setSiderCollapsed(collapsed)}
              trigger={null} // Ocultamos el trigger por defecto para añadir el nuestro
            >
              {currentProjectTreeView && currentProjectTreeView.length > 0 && (
                <TreeNavigation
                  data={currentProjectTreeView ?? []}
                  treeData={treeData}
                  selectedType={selectedType}
                  selectedId={selectedId}
                  onSelectionChange={handleSelectionChange}
                />
              )}
            </Sider>
            <Button
              type='text'
              icon={siderCollapsed ? <RightOutlined /> : <LeftOutlined />}
              onClick={() => setSiderCollapsed(!siderCollapsed)}
              style={{
                position: 'absolute',
                top: '0',
                left: '0',
                zIndex: 1,
                transition: 'left 0.2s',
                background: '#f2f2f2',
                borderLeft: siderCollapsed ? 'none' : '1px solid #ddd',
                borderRadius: siderCollapsed ? '0 4px 4px 0' : '4px 0 0 4px',
              }}
            />
            <Content style={{ padding: '0 24px', minHeight: 280 }}>
              {selectedType === 'node' && selectedId > 0 ? (
                nodes.find((node: NodeType) => node.id === selectedId) ? (
                  <>
                    <Col className='node-content-container'>
                      <h2>
                        {nodes.find((node: NodeType) => node.id === selectedId)?.name || t('Nodo no encontrado')}{' '}
                        {nodes.find((node: NodeType) => node.id === selectedId)?.effective_methodology?.name ? (
                          <small style={{ fontSize: '0.85em', color: '#9aa0a6' }}>
                            (
                            {nodes.find((node: NodeType) => node.id === selectedId)?.effective_methodology?.name})
                          </small>
                        ) : null}
                      </h2>
                      <div>
                        <b>{t('Descripción:')}</b>{' '}
                        <span
                          dangerouslySetInnerHTML={{
                            __html:
                              nodes
                                .find((node: NodeType) => node.id === selectedId)
                                ?.description?.replace(/\n/g, '<br />') || t('No disponible'),
                          }}
                        />
                      </div>

                      {nodes.find((node: NodeType) => node.id === selectedId)?.design_intention && (
                        <div>
                          <b>{t('Intención del Diseño:')}</b>{' '}
                          <span
                            dangerouslySetInnerHTML={{
                              __html:
                                nodes
                                  .find((node: NodeType) => node.id === selectedId)
                                  ?.design_intention?.replace(/\n/g, '<br />') || t('No disponible'),
                            }}
                          />
                        </div>
                      )}

                      {nodes.find((node: NodeType) => node.id === selectedId)?.special_consideration && (
                        <div>
                          <b>{t('P&ID:')}</b>{' '}
                          <span
                            dangerouslySetInnerHTML={{
                              __html:
                                nodes
                                  .find((node: NodeType) => node.id === selectedId)
                                  ?.special_consideration?.replace(/\n/g, '<br />') || t('No disponible'),
                            }}
                          />
                        </div>
                      )}

                      {nodes.find((node: NodeType) => node.id === selectedId)?.comments && (
                        <div>
                          <b>{t('Comentarios:')}</b>{' '}
                          <span
                            dangerouslySetInnerHTML={{
                              __html:
                                nodes
                                  .find((node: NodeType) => node.id === selectedId)
                                  ?.comments?.replace(/\n/g, '<br />') || t('No disponible'),
                            }}
                          />
                        </div>
                      )}

                      <Divider />

                      <Deviations
                        deviations={deviations}
                        nodeId={selectedId}
                        nodeOrder={nodeOrderMap[selectedId] ?? undefined}
                        methodology={
                          nodes.find((node: NodeType) => node.id === selectedId)?.effective_methodology ??
                          projectMethodology
                        }
                        labels={
                          nodes.find((node: NodeType) => node.id === selectedId)?.effective_methodology?.labels ??
                          projectLabels
                        }
                        getDeviations={() => getDeviations(selectedId) as any}
                        onSelectionChange={handleSelectionChange}
                        updateTree={() => getProjectTree()}
                      />
                    </Col>
                  </>
                ) : (
                  <p>{t('Nodo no encontrado')}</p>
                )
              ) : selectedType === 'deviation' && selectedId > 0 ? (
                <Deviation
                  id={selectedId}
                  nodeOrder={selectedDeviationNodeOrder}
                  deviationOrder={selectedDeviationOrder}
                  onNavigateToDeviation={(deviationId) => handleSelectionChange('deviation', deviationId)}
                  methodology={
                    nodes.find((node: NodeType) => node.id === selectedDeviationParentNodeId)?.effective_methodology ??
                    projectMethodology
                  }
                  labels={
                    nodes.find((node: NodeType) => node.id === selectedDeviationParentNodeId)?.effective_methodology
                      ?.labels ?? projectLabels
                  }
                />
              ) : (
                <NodeList
                  nodes={nodes as NodeType[]}
                  projectMethodologyName={projectMethodology?.name}
                  projectLabels={projectLabels}
                  projectMethodology={projectMethodology}
                  handleEdit={handleEdit as (node: NodeType) => void}
                  handleDelete={handleDelete as (node: NodeType) => void}
                  onSelectionChange={handleSelectionChange as (type: string, id: number) => void}
                  handleMoveUp={moveUp as (node: NodeType) => void}
                  handleMoveDown={moveDown as (node: NodeType) => void}
                  handleClone={handleClone as (node: NodeType) => void}
                  // <p>Seleccione un nodo o desviación del árbol para ver sus detalles.</p>
                />
              )}
            </Content>
          </Layout>
        </Col>
      </Row>
      <Tooltip placement='right' title={t('Ver matrices del proyecto')}>
        <Button
          type='primary'
          className='matrix-floating-button'
          icon={<AppstoreOutlined />}
          onClick={() => setIsMatrixOverlayVisible(true)}
          aria-label={t('Ver matrices del proyecto')}
        ></Button>
      </Tooltip>
      <MatrixViewerOverlay
        projectId={project_id}
        open={isMatrixOverlayVisible}
        onClose={() => setIsMatrixOverlayVisible(false)}
      />
    </>
  );
};

export default Nodes;
